// usePlaceStreams.js
import { useEffect, useState } from 'react';

import { useHomeLocation } from '../utils/homeLocation';
import { LIVE_REFRESH_MS, fetchPlaceStreams } from '../utils/streams';

/**
 * usePlaceStreams
 *
 * /streams/place for the reader's saved location, refreshed once a minute as
 * the streams guide asks, and never while the tab is hidden.
 *
 * What is sent, from the saved home location (utils/homeLocation):
 *
 *   geoid        the city's Census GEOID (7 digits), which gives city, county,
 *                state and federal streams -- the API finds the county itself
 *   countyGeoid  the county's (5 digits), when no city is known: county, state
 *                and federal
 *   stateAbbr    the state alone: state and federal
 *   nothing      the national federal bodies only
 *
 * The street address is never sent, though the API would take one and would
 * then narrow federal streams to the reader's own House member. homeLocation
 * holds the address only to resolve districts once, after which "the street
 * address never leaves the device again" -- and this refreshes every minute.
 * The cost is that federal streams include every House member from the state
 * rather than just the reader's own; the API says so in its notes.
 *
 * GEOIDs are checked for shape before use. homeLocation's `geoid` is whatever
 * /officials/directory echoed, so a value that is not a 7-digit place or
 * 5-digit county code falls through to the next option rather than being sent
 * as a bad parameter.
 *
 * A failed refresh keeps the last answer: `error` is set only when there is
 * nothing to show at all. Returns { data, loading, error }.
 */
const CITY_GEOID = /^\d{7}$/;
const COUNTY_GEOID = /^\d{5}$/;

// The most precise GEOID the saved location holds: a city's, else a county's.
// `geoid` is checked for both shapes, since what /officials/directory echoes
// there can be either depending on what it resolved.
const placeGeoidFor = (location) => {
  const geoid = location?.geoid ? String(location.geoid).trim() : '';
  const countyGeoid = location?.countyGeoid ? String(location.countyGeoid).trim() : '';
  if (CITY_GEOID.test(geoid)) return geoid;
  if (COUNTY_GEOID.test(countyGeoid)) return countyGeoid;
  if (COUNTY_GEOID.test(geoid)) return geoid;
  return null;
};

export default function usePlaceStreams({ enabled = true } = {}) {
  const { location } = useHomeLocation();
  const geoid = placeGeoidFor(location);
  const stateAbbr = location?.stateAbbr || null;

  const [result, setResult] = useState({ data: null, loading: enabled, error: null });

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    let controller = null;

    const load = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (controller) controller.abort();
      controller = new AbortController();

      fetchPlaceStreams({ geoid, state: stateAbbr }, { signal: controller.signal })
        .then((data) => {
          if (active) setResult({ data, loading: false, error: null });
        })
        .catch((error) => {
          if (!active || error?.name === 'AbortError') return;
          setResult((current) => ({
            data: current.data,
            loading: false,
            error: current.data ? null : error,
          }));
        });
    };

    setResult((current) => ({ ...current, loading: !current.data }));
    load();
    const timer = setInterval(load, LIVE_REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      active = false;
      clearInterval(timer);
      if (controller) controller.abort();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [geoid, stateAbbr, enabled]);

  return result;
}